import * as _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { ShrinkContent } from '../ShrinkContent';
import ItemsSummaryList from './ItemsSummaryList';
import {
  asCurrency,
  asDecimal,
  asDynamicDecimalWithUnit,
} from '../../utils/formatters';
import { useFilterYears } from '../../utils/hooks';
import { avg, last, sum } from '../../utils/aggregators';
import { useFilterContext } from '../../contexts/FilterContext';
import { YIELD_UNITS_BY_DEFAULT } from '../../constants/units';
import { useUnitsMap } from '../../contexts/UnitContext';

const yieldTypes = {
  YIELD_WEIGHT: 'yieldWeight',
  YIELD_VOLUME: 'yieldVolume',
};

const metrics = {
  [yieldTypes.YIELD_WEIGHT]: {
    accessor: yieldTypes.YIELD_WEIGHT,
    aggregator: avg,
    format: (value, item) => asDynamicDecimalWithUnit(item.unit)(value),
    csvFormat: asDecimal,
    viewMoreLink: '../yield',
  },
  [yieldTypes.YIELD_VOLUME]: {
    accessor: yieldTypes.YIELD_VOLUME,
    aggregator: sum,
    format: (value, item) => asDynamicDecimalWithUnit(item.unit)(value),
    csvFormat: asDecimal,
    viewMoreLink: '../yield',
  },
  revenue: {
    accessor: 'revenue',
    aggregator: avg,
    format: asCurrency,
    viewMoreLink: '../revenue',
  },
  carbon: {
    accessor: 'carbon',
    aggregator: last,
    format: asDynamicDecimalWithUnit('tons', 'ton'),
    csvFormat: asDecimal,
    viewMoreLink: '../carbon',
  },
};

const options = ['yield', 'revenue', 'carbon'].map((option) => ({
  key: option,
  value: option,
  name: _.capitalize(option),
}));

const CropOutputsSummary = ({ cropAggregates = [], className }) => {
  const filterYears = useFilterYears();
  const { years } = useFilterContext().state;
  const [chartOption, setChartOption] = useState(options[0]);
  const yieldUnitsMap = useUnitsMap(YIELD_UNITS_BY_DEFAULT);
  const [yieldOption, setYieldOption] = useState(yieldTypes.YIELD_WEIGHT);
  const metricKey = chartOption.key === 'yield' ? yieldOption : chartOption.key;
  const valueColumn = metrics[metricKey];
  const { accessor, aggregator } = valueColumn || {};

  const transformedItems = useMemo(
    () =>
      cropAggregates.map(
        ({
          crop: { id, name, color },
          ecologicalSummary: { carbonTonsByYear },
          yieldAggregates,
          economicSummary: { revenueByYear },
        }) => {
          // Assumes for now that there is zero or one yield aggregate per crop.
          const { yieldByYear = [], yield: { unit } = {} } =
            yieldAggregates[0] || {};
          const { displayUnit = unit, transformToUnit = _.identity } =
            yieldUnitsMap[unit] || {};
          const yieldType =
            unit === 'bft' ? yieldTypes.YIELD_VOLUME : yieldTypes.YIELD_WEIGHT;
          return {
            id,
            name,
            color,
            // Only show carbon if there is at least one year with a non-zero value.
            carbon: ((filteredCarbonTonsByYear) =>
              filteredCarbonTonsByYear.some((c) => c > 0)
                ? metrics.carbon.aggregator(filteredCarbonTonsByYear)
                : null)(filterYears(carbonTonsByYear)),
            yieldType,
            [yieldType]: ((filteredYieldByYear) =>
              filteredYieldByYear.some((y) => y > 0)
                ? transformToUnit(
                    metrics[yieldType].aggregator(filteredYieldByYear),
                  )
                : null)(filterYears(yieldByYear)),
            revenue: metrics.revenue.aggregator(filterYears(revenueByYear)),
            unit: displayUnit,
          };
        },
      ),
    [cropAggregates, filterYears, yieldUnitsMap],
  );

  const yieldOptions = useMemo(
    () =>
      _.uniqBy(
        transformedItems
          .filter(({ unit }) => unit)
          .map(({ unit, yieldType }) => ({
            key: yieldType,
            value: yieldType,
            name: _.capitalize(unit),
          })),
        'key',
      ),
    [transformedItems],
  );

  useEffect(() => {
    // Ensure we have a yield option with available data
    if (
      !yieldOptions.find(({ value }) => value === yieldOption) &&
      yieldOptions[0]
    ) {
      setYieldOption(yieldOptions[0].value);
    }
  }, [yieldOption, yieldOptions]);

  const orderedItems = useMemo(
    () =>
      _.orderBy(
        transformedItems.filter((item) => item[accessor] != null),
        accessor,
        'desc',
      ),
    [transformedItems, accessor],
  );

  const dropdownProps = {
    options,
    selectedOption: chartOption.name,
    setOption: setChartOption,
  };

  const secondaryDropdownProps = {
    options: yieldOptions,
    selectedOption: yieldOptions.find(({ value }) => value === yieldOption)
      ?.name,
    setOption: ({ value }) => setYieldOption(value),
  };

  const aggregationInfo = `${years} Year ${
    aggregator === avg ? 'Average' : 'Total'
  }`;

  const valueColumnWithUnit = {
    ...valueColumn,
    // Used only for csv export
    unit:
      (chartOption.value === 'yield' &&
        yieldOptions.find(({ value }) => value === yieldOption)?.name) ||
      '',
  };

  return (
    <ShrinkContent className={className}>
      <ItemsSummaryList
        items={orderedItems}
        valueColumn={valueColumnWithUnit}
        dropdownProps={dropdownProps}
        secondaryDropdownProps={
          chartOption.value === 'yield' && yieldOptions.length > 1
            ? secondaryDropdownProps
            : undefined
        }
        aggregationInfo={aggregationInfo}
        viewMoreLink={valueColumn?.viewMoreLink}
      />
    </ShrinkContent>
  );
};

export default CropOutputsSummary;
